import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from '../../intl/messages';
import { WARNING_TEXT_COLOR, clusterMarkers, formatDurationMs, formatOffset } from '../../utils/mobileCameraLog';
import { getEvtMessage } from './logEvents';

const LABEL_WIDTH = 90;
const RIGHT_PADDING = 12;
const MARKER_STRIP_HEIGHT = 20;
const LANE_HEIGHT = 24;
const LANE_BAR_HEIGHT = 16;
const AXIS_HEIGHT = 24;
const TICK_COUNT = 6;

const LANE_KEYS = ['socket', 'net', 'rec', 'queue'];

const useMeasuredWidth = () => {
	const ref = useRef(null);
	const [width, setWidth] = useState(0);

	useEffect(() => {
		if (!ref.current) return undefined;

		const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return [ref, width];
};

const LogTimeline = ({ entries, summary, intervals, markerEntries, queueSeries, selectedRange, onSelectRange }) => {
	const intl = useIntl();
	const theme = useTheme();
	const [containerRef, width] = useMeasuredWidth();
	const [hover, setHover] = useState(null); // { x, y, lines: string[] }
	const [hoverX, setHoverX] = useState(null);

	const firstEntryAt = summary?.firstEntryAt ? new Date(summary.firstEntryAt).getTime() : entries[0]?.t;
	const durationMs = summary?.durationMs ?? (entries.length ? entries[entries.length - 1].t - entries[0].t : 0);
	const tooShort = entries.length <= 1 || !durationMs || durationMs < 10000;

	const plotWidth = Math.max(0, width - LABEL_WIDTH - RIGHT_PADDING);
	const xScale = (offsetMs) => LABEL_WIDTH + (durationMs ? (offsetMs / durationMs) * plotWidth : 0);

	const clusters = useMemo(
		() => clusterMarkers(markerEntries, plotWidth, durationMs),
		[markerEntries, plotWidth, durationMs]
	);

	const maxQueue = useMemo(() => Math.max(1, ...queueSeries.map((p) => p.value), 0), [queueSeries]);

	const svgHeight = MARKER_STRIP_HEIGHT + LANE_HEIGHT * LANE_KEYS.length + AXIS_HEIGHT;

	const ticks = useMemo(() => {
		if (!durationMs || !firstEntryAt) return [];
		return Array.from({ length: TICK_COUNT }, (_, i) => {
			const offsetMs = (durationMs / (TICK_COUNT - 1)) * i;
			return { offsetMs, label: intl.formatDate(firstEntryAt + offsetMs, { hour: '2-digit', minute: '2-digit' }) };
		});
	}, [durationMs, firstEntryAt, intl]);

	const laneColors = {
		socket: { up: theme.palette.success.light, upStroke: theme.palette.success.main },
		net: { up: theme.palette.primary.light, upStroke: theme.palette.grey[400] },
		rec: { up: theme.palette.success.main, upStroke: theme.palette.success.main },
	};

	const laneMessages = {
		socket: messages.mobileLogLaneSocket,
		net: messages.mobileLogLaneNet,
		rec: messages.mobileLogLaneRec,
		queue: messages.mobileLogLaneQueue,
	};

	const laneY = (index) => MARKER_STRIP_HEIGHT + index * LANE_HEIGHT;

	const clearHover = () => {
		setHover(null);
		setHoverX(null);
	};

	// shared by the floating tooltip and the aria-label of the same element, so screen readers get the same info
	const getIntervalLabel = (laneKey, interval) => {
		const duration = formatDurationMs(intl, (interval.end ?? durationMs) - interval.start);
		const isDown = interval.state === 'down';
		const stateLabel = isDown
			? intl.formatMessage(messages.mobileLogLegendOutage)
			: intl.formatMessage(laneMessages[laneKey]);
		return `${stateLabel} · ${formatOffset(interval.start)}–${interval.open ? '…' : formatOffset(interval.end)} · ${duration}`;
	};

	const getClusterLines = (cluster) => {
		const shown = cluster.items.slice(0, 5).map((item) => {
			const msg = getEvtMessage(item.evt);
			const text = msg ? intl.formatMessage(msg) : intl.formatMessage(messages.mobileLogEvtUnknown);
			return `${formatOffset(item.offsetMs)} ${text}`;
		});
		if (cluster.items.length > 5) {
			shown.push(intl.formatMessage(messages.mobileLogMoreEvents, { count: cluster.items.length - 5 }));
		}
		return shown;
	};

	const showIntervalTooltip = (e, laneKey, interval) => {
		const rect = containerRef.current.getBoundingClientRect();
		setHover({ x: e.clientX - rect.left, y: e.clientY - rect.top, lines: [getIntervalLabel(laneKey, interval)] });
	};

	const showMarkerTooltip = (e, cluster) => {
		const rect = containerRef.current.getBoundingClientRect();
		setHover({ x: e.clientX - rect.left, y: e.clientY - rect.top, lines: getClusterLines(cluster) });
	};

	const selectInterval = (interval) => onSelectRange({ start: interval.start, end: interval.end ?? durationMs });
	const selectCluster = (cluster) => {
		const times = cluster.items.map((i) => i.offsetMs);
		onSelectRange({ start: Math.max(0, Math.min(...times) - 1000), end: Math.max(...times) + 1000 });
	};

	const markerShape = (cx, cy, severity, count, label, onEnter, onClick) => {
		const color =
			severity === 'error' ? theme.palette.error.main : severity === 'warning' ? theme.palette.warning.main : theme.palette.primary.dark;
		const common = {
			onMouseEnter: onEnter,
			onMouseLeave: clearHover,
			onClick,
			onKeyDown: (e) => e.key === 'Enter' && onClick(e),
			tabIndex: 0,
			role: 'button',
			'aria-label': label,
			style: { cursor: 'pointer' },
		};

		if (count > 1) {
			return (
				<g {...common}>
					<circle cx={cx} cy={cy} r={8} fill={color} />
					<text x={cx} y={cy + 3} textAnchor="middle" fontSize={9} fill={severity === 'warning' ? '#000' : '#fff'}>
						×{count}
					</text>
				</g>
			);
		}

		if (severity === 'error') {
			return (
				<rect
					{...common}
					x={cx - 5}
					y={cy - 5}
					width={10}
					height={10}
					fill={color}
					transform={`rotate(45 ${cx} ${cy})`}
				/>
			);
		}

		if (severity === 'warning') {
			return <polygon {...common} points={`${cx},${cy - 6} ${cx - 6},${cy + 5} ${cx + 6},${cy + 5}`} fill={color} stroke={WARNING_TEXT_COLOR} />;
		}

		return <circle {...common} cx={cx} cy={cy} r={4} fill={color} />;
	};

	if (tooShort) {
		return (
			<Box sx={{ height: 200 }}>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					<FormattedMessage {...messages.mobileLogTimeline} />
				</Typography>
				<Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
					<FormattedMessage {...messages.mobileLogTooShort} values={{ seconds: Math.round((durationMs ?? 0) / 1000) }} />
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ height: 200 }}>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
				<Typography variant="subtitle2">
					<FormattedMessage {...messages.mobileLogTimeline} />
				</Typography>
				<Typography variant="caption" color="text.secondary">
					◆ <FormattedMessage {...messages.mobileLogLegendError} /> · ▲ <FormattedMessage {...messages.mobileLogLegendWarning} /> · ●{' '}
					<FormattedMessage {...messages.mobileLogLegendMilestone} /> · ▨ <FormattedMessage {...messages.mobileLogLegendOutage} />
				</Typography>
			</Box>
			<Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
				{width > 0 && (
					<svg
						width={width}
						height={svgHeight}
						aria-label={intl.formatMessage(messages.mobileLogTimeline)}
						onClick={() => onSelectRange(null)}
						style={{ display: 'block', userSelect: 'none' }}
					>
						<defs>
							<pattern id="mobileLogHatch" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
								<rect width={6} height={6} fill={theme.palette.error.light} />
								<line x1={0} y1={0} x2={0} y2={6} stroke={theme.palette.error.main} strokeOpacity={0.4} strokeWidth={2} />
							</pattern>
						</defs>

						{selectedRange && (
							<rect
								x={xScale(selectedRange.start)}
								y={0}
								width={Math.max(1, xScale(selectedRange.end) - xScale(selectedRange.start))}
								height={svgHeight - AXIS_HEIGHT}
								fill={theme.palette.primary.main}
								opacity={0.08}
							/>
						)}

						{LANE_KEYS.map((laneKey, index) => {
							const y = laneY(index);
							return (
								<g key={laneKey}>
									<text x={LABEL_WIDTH - 8} y={y + LANE_HEIGHT / 2 + 4} textAnchor="end" fontSize={12} fontWeight={500} fill={theme.palette.text.secondary}>
										<FormattedMessage {...laneMessages[laneKey]} />
									</text>
									<line
										x1={LABEL_WIDTH}
										x2={width - RIGHT_PADDING}
										y1={y + LANE_HEIGHT / 2}
										y2={y + LANE_HEIGHT / 2}
										stroke={theme.palette.grey[300]}
										strokeWidth={2}
									/>
								</g>
							);
						})}

						{['socket', 'net', 'rec'].map((laneKey, index) => {
							const y = laneY(index);
							return (intervals[laneKey] ?? []).map((interval, i) => {
								const x1 = xScale(interval.start);
								const x2 = xScale(interval.end ?? durationMs);
								const isDown = interval.state === 'down';
								return (
									<rect
										key={`${laneKey}-${i}`}
										x={x1}
										y={y + (LANE_HEIGHT - LANE_BAR_HEIGHT) / 2}
										width={Math.max(1, x2 - x1)}
										height={LANE_BAR_HEIGHT}
										rx={2}
										fill={isDown ? 'url(#mobileLogHatch)' : laneColors[laneKey].up}
										stroke={isDown ? theme.palette.error.main : laneColors[laneKey].upStroke}
										strokeWidth={1}
										tabIndex={0}
										role="button"
										aria-label={getIntervalLabel(laneKey, interval)}
										style={{ cursor: 'pointer' }}
										onMouseEnter={(e) => {
											e.stopPropagation();
											showIntervalTooltip(e, laneKey, interval);
										}}
										onMouseLeave={clearHover}
										onKeyDown={(e) => e.key === 'Enter' && selectInterval(interval)}
										onClick={(e) => {
											e.stopPropagation();
											selectInterval(interval);
										}}
									/>
								);
							});
						})}

						{queueSeries.length > 1 &&
							(() => {
								const y = laneY(3);
								const baseline = y + LANE_HEIGHT - 2;
								const points = queueSeries.map((p) => [xScale(p.offsetMs), baseline - (p.value / maxQueue) * (LANE_BAR_HEIGHT - 2)]);
								const areaPath =
									`M ${points[0][0]},${baseline} ` +
									points.map(([x, py]) => `L ${x},${py}`).join(' ') +
									` L ${points[points.length - 1][0]},${baseline} Z`;
								const linePath = `M ${points.map(([x, py]) => `${x},${py}`).join(' L ')}`;
								return (
									<g>
										<path d={areaPath} fill={theme.palette.primary.main} opacity={0.25} />
										<path d={linePath} fill="none" stroke={theme.palette.primary.dark} strokeWidth={1.5} />
										<text x={width - RIGHT_PADDING} y={y + 10} textAnchor="end" fontSize={10} fill={theme.palette.text.secondary}>
											{intl.formatMessage(messages.mobileLogQueueMax, { count: maxQueue })}
										</text>
									</g>
								);
							})()}

						{clusters.map((cluster, i) => {
							const cx = LABEL_WIDTH + cluster.x;
							const cy = MARKER_STRIP_HEIGHT / 2;
							return (
								<g key={i}>
									{markerShape(
										cx,
										cy,
										cluster.severity,
										cluster.count,
										getClusterLines(cluster).join(', '),
										(e) => {
											e.stopPropagation();
											showMarkerTooltip(e, cluster);
											setHoverX(cx);
										},
										(e) => {
											e.stopPropagation();
											selectCluster(cluster);
										}
									)}
								</g>
							);
						})}

						{hoverX != null && <line x1={hoverX} x2={hoverX} y1={0} y2={svgHeight - AXIS_HEIGHT} stroke={theme.palette.grey[500]} strokeDasharray="2,2" />}

						{ticks.map((tick, i) => (
							<text
								key={i}
								x={xScale(tick.offsetMs)}
								y={svgHeight - AXIS_HEIGHT + 16}
								textAnchor={i === 0 ? 'start' : i === ticks.length - 1 ? 'end' : 'middle'}
								fontSize={12}
								fill={theme.palette.text.secondary}
							>
								{tick.label}
							</text>
						))}
					</svg>
				)}
				{hover && (
					<Box
						sx={{
							position: 'absolute',
							left: Math.min(hover.x + 10, Math.max(0, width - 260)),
							top: Math.max(0, hover.y - 10),
							bgcolor: 'grey.900',
							color: '#fff',
							p: 1,
							borderRadius: 1,
							fontSize: 12,
							pointerEvents: 'none',
							zIndex: 1,
							maxWidth: 260,
						}}
					>
						{hover.lines.map((line, i) => (
							<div key={i}>{line}</div>
						))}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default LogTimeline;
